import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './WastageThresholdApprovals.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE_URL = (API_BASE_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');

export default function WastageThresholdApprovals({ goTo }) {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const [confirmModal, setConfirmModal] = useState({ show: false, approvalId: null, type: null });

  // Get user info from localStorage and verify role from backend
  useEffect(() => {
    const initUser = async () => {
      try {
        const userStr = window.localStorage.getItem('user');
        if (!userStr) {
          setError('User not logged in');
          setLoading(false);
          return;
        }
        const user = JSON.parse(userStr);
        const resolvedUserId = user.id || user.user_id || user.userId;
        const storedBusinessId = localStorage.getItem('businessId');
        const resolvedBusinessId = storedBusinessId || user.business_id || user.businessId;
        setUserId(resolvedUserId);
        setBusinessId(resolvedBusinessId);

        if (!resolvedUserId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }

        // Fetch role from backend
        const resp = await fetch(`${BASE_URL}/api/users/${resolvedUserId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-Id': String(resolvedBusinessId)
          }
        });
        if (resp.ok) {
          const data = await resp.json();
          const roleName = (data.data?.role_name || '').toLowerCase();
          setUserRole(roleName);
          const isOwnerOrAdmin = roleName.includes('owner') || roleName.includes('admin');
          if (!isOwnerOrAdmin) {
            setError('Only owners can access approval requests');
            setLoading(false);
            return;
          }
        } else {
          setError('Failed to verify user role');
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Failed to get user info:', e);
        setError('Failed to load user information');
        setLoading(false);
      }
    };
    initUser();
  }, []);

  // Load pending approval requests
  useEffect(() => {
    if (!businessId || !userId) return;
    // Check role includes owner or admin
    const isOwnerOrAdmin = userRole && (userRole.includes('owner') || userRole.includes('admin'));
    if (!isOwnerOrAdmin) return;
    
    const loadPendingRequests = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${BASE_URL}/api/wastage/threshold-approvals/pending?userId=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-Id': businessId
            }
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to load approval requests');
        }

        setPendingRequests(result.data || []);
      } catch (e) {
        console.error('Error loading approval requests:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    const loadHistoryRequests = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/wastage/threshold-approvals/history?userId=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Business-Id': businessId
            }
          }
        );

        const result = await response.json();

        if (response.ok) {
          setHistoryRequests(result.data || []);
        }
      } catch (e) {
        console.error('Error loading history requests:', e);
      }
    };

    loadPendingRequests();
    loadHistoryRequests();
  }, [businessId, userId, userRole]);

  const handleApprove = async (approvalId) => {
    setConfirmModal({ show: true, approvalId, type: 'approve' });
  };

  const confirmApprove = async () => {
    const approvalId = confirmModal.approvalId;
    setConfirmModal({ show: false, approvalId: null, type: null });
    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-Id': businessId
          },
          body: JSON.stringify({
            userId: userId,
            review_notes: reviewNotes[approvalId] || ''
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to approve request');
      }

      setConfirmModal({ show: true, approvalId: null, type: 'success' });
      
      // Remove from pending list
      setPendingRequests(prev => prev.filter(r => r.approval_id !== approvalId));
      setReviewNotes(prev => {
        const updated = { ...prev };
        delete updated[approvalId];
        return updated;
      });

    } catch (e) {
      console.error('Error approving request:', e);
      alert('❌ ' + e.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (approvalId) => {
    const notes = reviewNotes[approvalId] || '';
    
    if (!notes.trim()) {
      alert('Please provide a reason for rejection in the notes field');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this threshold change?')) {
      return;
    }

    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-Id': businessId
          },
          body: JSON.stringify({
            userId: userId,
            review_notes: notes
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reject request');
      }

      alert('✅ Threshold request rejected successfully!');
      
      // Remove from pending list
      setPendingRequests(prev => prev.filter(r => r.approval_id !== approvalId));
      setReviewNotes(prev => {
        const updated = { ...prev };
        delete updated[approvalId];
        return updated;
      });

    } catch (e) {
      console.error('Error rejecting request:', e);
      alert('❌ ' + e.message);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="threshold-approvals-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading approval requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="threshold-approvals-container">
        <div className="error-state">
          <h3>⚠️ {error}</h3>
          {goTo && (
            <button onClick={() => goTo('dashboard')} className="btn-secondary">
              Go to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="threshold-approvals-container">
      <div className="approvals-header">
        <div className="header-title-container" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button
            onClick={() => {
              if (goTo) {
                goTo('dashboard');
              } else if (window && window.history) {
                window.history.back();
              }
            }}
            style={{
              width: '40px',
              height: '40px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '24px', color: '#333' }} />
          </button>
          <h2 style={{ margin: 0, fontSize: 'clamp(20px, 4vw, 24px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Wastage Threshold Approval</h2>
        </div>
        
        <div className="stock-tabs" style={{ display: 'flex', width: '100%' }}>
          <button 
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            style={{ flex: 1 }}
          >
            Pending
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
            style={{ flex: 1 }}
          >
            History
          </button>
        </div>
      </div>

      {activeTab === 'pending' && (
        <>
          {pendingRequests.length === 0 ? (
            <div className="empty-state pending-empty-state">
              <h3>No Pending Requests</h3>
            </div>
          ) : (
            <div className="approvals-list">
              {pendingRequests.map((request) => (
              <div key={request.approval_id} className="approval-card">
                <div className="approval-header">
                  <div className="approval-title">
                    <h3>{request.item_name}</h3>
                    <span className="approval-type-badge">
                      {request.threshold_type === 'menu_item' ? 'Menu Item' : 'Raw Ingredient'}
                    </span>
                  </div>
                  <span className="approval-time">
                    {new Date(request.created_at).toLocaleString()}
                  </span>
                </div>

                {request.evidence_image_url && (
                  <div className="approval-image" style={{ marginBottom: '15px' }}>
                    <img 
                      src={(() => {
                        const url = request.evidence_image_url;
                        // GCS URLs - use directly
                        if (url.includes('storage.googleapis.com')) {
                          return url;
                        }
                        // Absolute URLs (http/https) - use directly
                        if (url.startsWith('http://') || url.startsWith('https://')) {
                          return url;
                        }
                        // Relative paths starting with /uploads - convert to GCS
                        if (url.startsWith('/uploads')) {
                          const gcsBaseUrl = 'https://storage.googleapis.com/menuimages001';
                          const gcsUrl = `${gcsBaseUrl}${url}`;
                          console.warn('⚠️ Converting relative path to GCS:', url, '→', gcsUrl);
                          return gcsUrl;
                        }
                        // Fallback
                        return url;
                      })()}
                      alt={`Evidence for ${request.item_name}`}
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '200px', 
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: '1px solid #e5e7eb'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(e.target.src, '_blank');
                      }}
                      onError={(e) => { 
                        console.error('❌ Failed to load approval image:', request.evidence_image_url);
                        console.error('   Attempted URL:', e.target.src);
                        if (request.evidence_image_id && !e.target.dataset.proxyAttempted) {
                          e.target.dataset.proxyAttempted = 'true';
                          e.target.src = `${BASE_URL}/api/wastage/photos/proxy/${request.evidence_image_id}`;
                        } else {
                          e.target.style.display = 'none';
                          console.error('❌ All image loading attempts failed');
                          // Show error message
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.image-error')) {
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'image-error';
                            errorDiv.style.cssText = 'padding: 12px; background: #fee; border: 1px solid #fcc; border-radius: 8px; color: #c00; text-align: center; font-size: 13px;';
                            errorDiv.textContent = '📸 Image not available - File may have been moved or deleted';
                            parent.appendChild(errorDiv);
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="approval-details">
                  <div className="detail-row">
                    <span className="label">Requested by:</span>
                    <span className="value">{request.requested_by_name}</span>
                </div>
                
                {request.category && (
                  <div className="detail-row">
                    <span className="label">Category:</span>
                    <span className="value">{request.category}</span>
                  </div>
                )}

                <div className="threshold-change">
                  <div className="threshold-box current">
                    <span className="threshold-label">Current limits</span>
                    <span className="threshold-value">
                      {request.current_threshold || 'Not Set'} {request.threshold_unit || 'plates'}
                    </span>
                  </div>
                  
                  <div className="arrow">→</div>
                  
                  <div className="threshold-box requested">
                    <span className="threshold-label">Requested limits</span>
                    <span className="threshold-value">
                      {request.requested_threshold} {request.threshold_unit || 'plates'}
                    </span>
                  </div>
                </div>

                {request.justification && (() => {
                  const reasonIndex = request.justification.indexOf('Reason:');
                  const reasonText = reasonIndex !== -1 ? request.justification.substring(reasonIndex) : request.justification;
                  return (
                    <div className="justification">
                      <span className="label">Justification:</span>
                      <p>{reasonText}</p>
                    </div>
                  );
                })()}

                <div className="review-notes-section">
                  <label htmlFor={`notes-${request.approval_id}`}>
                    Review Notes (required for rejection):
                  </label>
                  <textarea
                    id={`notes-${request.approval_id}`}
                    value={reviewNotes[request.approval_id] || ''}
                    onChange={(e) => setReviewNotes(prev => ({
                      ...prev,
                      [request.approval_id]: e.target.value
                    }))}
                    placeholder="Add notes about your decision..."
                    rows={3}
                    disabled={processingId === request.approval_id}
                  />
                </div>
              </div>

              <div className="approval-actions">
                <button
                  onClick={() => handleReject(request.approval_id)}
                  disabled={processingId === request.approval_id}
                  className="btn-reject"
                >
                  {processingId === request.approval_id ? 'Processing...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleApprove(request.approval_id)}
                  disabled={processingId === request.approval_id}
                  className="btn-approve"
                >
                  {processingId === request.approval_id ? 'Processing...' : 'Approve'}
                </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
      )}

      {activeTab === 'history' && (
        <div className="approvals-list">
          {historyRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No History</h3>
              <p>No approval requests have been processed yet</p>
            </div>
          ) : (
            historyRequests.map((request) => (
              <div key={request.approval_id} className="approval-card" style={{ opacity: 0.9 }}>
                <div className="approval-header">
                  <div className="approval-title">
                    <h3>{request.item_name}</h3>
                    <span className={`approval-status-badge status-${request.status}`}>
                      {request.status.toUpperCase()}
                    </span>
                  </div>
                  <span className="approval-time">
                    {new Date(request.created_at).toLocaleString()}
                  </span>
                </div>

                {(request.evidence_image_id || request.evidence_image_url) && (
                  <div className="approval-image" style={{ marginBottom: '15px' }}>
                    <img 
                      src={
                        request.evidence_image_id 
                          ? `${BASE_URL}/api/wastage/photos/proxy/${request.evidence_image_id}` 
                          : (request.evidence_image_url?.startsWith('http') ? request.evidence_image_url : `${BASE_URL}${request.evidence_image_url}`)
                      }
                      alt={`Evidence for ${request.item_name}`}
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                      onError={(e) => { 
                        console.error('[WastageThresholdApprovals-History] ❌ Image failed to load for', request.item_name);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div className="approval-details">
                  <div className="detail-row">
                    <span className="label">Requested by:</span>
                    <span className="value">{request.requested_by_name}</span>
                  </div>

                  <div className="threshold-change">
                    <div className="threshold-box current">
                      <span className="threshold-label">Current limit</span>
                      <span className="threshold-value">
                        {request.current_threshold || 'Not Set'} {request.threshold_unit || 'plates'}
                      </span>
                    </div>
                    
                    <div className="arrow">→</div>
                    
                    <div className="threshold-box requested">
                      <span className="threshold-label">Requested limit</span>
                      <span className="threshold-value">
                        {request.requested_threshold} {request.threshold_unit || 'plates'}
                      </span>
                    </div>
                  </div>

                  {request.justification && (() => {
                    const reasonIndex = request.justification.indexOf('Reason:');
                    const reasonText = reasonIndex !== -1 ? request.justification.substring(reasonIndex) : request.justification;
                    return (
                      <div className="justification">
                        <span className="label">Justification:</span>
                        <p>{reasonText}</p>
                      </div>
                    );
                  })()}

                  {request.review_notes && (
                    <div className="review-notes-history" style={{ marginTop: '10px' }}>
                      <span className="label">Notes</span>
                      <div className="review-notes-box">
                        {request.review_notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmModal({ show: false, approvalId: null, type: null })}>
          <div className="confirm-modal-card" onClick={(e) => e.stopPropagation()}>
            {confirmModal.type === 'approve' ? (
              <>
                <h3>Confirm Approval</h3>
                <p>Are you sure you want to approve this threshold change?</p>
                <div className="confirm-modal-actions">
                  <button 
                    className="btn-cancel" 
                    onClick={() => setConfirmModal({ show: false, approvalId: null, type: null })}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-confirm" 
                    onClick={confirmApprove}
                  >
                    OK
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Success</h3>
                <p>Threshold approved and applied successfully!</p>
                <div className="confirm-modal-actions single-button">
                  <button 
                    className="btn-confirm" 
                    onClick={() => setConfirmModal({ show: false, approvalId: null, type: null })}
                  >
                    OK
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
